import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';

const Formats = () => {
  const { t } = useLanguage();

  const formats = [
    {
      icon: "MapPin",
      badge: t("formats.items.inperson.badge"),
      title: t("formats.items.inperson.title"),
      desc: t("formats.items.inperson.desc"),
      features: [t("formats.items.inperson.feat1"), t("formats.items.inperson.feat2"), t("formats.items.inperson.feat3")]
    },
    {
      icon: "Monitor",
      badge: t("formats.items.hybrid.badge"),
      title: t("formats.items.hybrid.title"),
      desc: t("formats.items.hybrid.desc"),
      features: [t("formats.items.hybrid.feat1"), t("formats.items.hybrid.feat2"), t("formats.items.hybrid.feat3")]
    },
    {
      icon: "Video",
      badge: t("formats.items.virtual.badge"),
      title: t("formats.items.virtual.title"),
      desc: t("formats.items.virtual.desc"),
      features: [t("formats.items.virtual.feat1"), t("formats.items.virtual.feat2"), t("formats.items.virtual.feat3")]
    }
  ];

  return (
    <section id="formats" className="py-24 bg-slate-900 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
      
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-block px-3 py-1 rounded-full bg-slate-800 text-slate-300 text-xs font-semibold uppercase tracking-wider mb-4 border border-slate-700">
            {t('formats.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
            {t('formats.title')}
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {t('formats.desc')}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {formats.map((format, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:bg-slate-800 transition-colors"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-indigo-500/20 text-indigo-400 flex items-center justify-center">
                  <SafeIcon name={format.icon} className="w-5 h-5" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400 bg-slate-900/50 px-2 py-1 rounded">
                  {format.badge}
                </span>
              </div>
              
              <h3 className="text-xl font-bold text-white mb-3">{format.title}</h3>
              <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                {format.desc}
              </p>
              
              <ul className="space-y-3">
                {format.features.map((feat, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                    <SafeIcon name="CheckCircle" className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Formats;